/**
 * Trishul designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_shivratri_holi`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const trishulDesignsPack: ToilePack = {
    slug: 'trishul-designs',
    category: 'faith',
    name: 'Trishul designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'damru', label: 'With damru', subject: 'a trishul with a small damru tied to the shaft by a ribbon, the three prongs straight and sharp'},
        {key: 'om', label: 'With Om', subject: 'a trishul with the Om sign set where the three prongs meet and a crescent moon above the centre prong'},
        {key: 'snake', label: 'With snake', subject: 'a trishul with a cobra wound up the shaft, its hood spread just under the prongs, and three lines of ash across the middle'},
    ],
    styles: [
        {key: 'flash', label: 'Tattoo flash', style: 'tattoo flash, bold black outlines with heavy black shading and dotwork', palette: 'black ink only', framing: 'The emblem is centred and upright with a wide margin on a plain white background.'},
        {key: 'sticker', label: 'Die-cut sticker', style: 'a die-cut sticker design, flat fills with a thick white border cut round the whole shape', palette: 'saffron orange, deep blue, white and black', framing: 'The emblem fills most of the square, centred on a plain light grey background.'},
        {key: 'gold', label: 'Gold on black', style: 'an ornate engraved emblem, fine gold line and filigree with smooth metallic shading', palette: 'gold on a solid black ground', framing: 'The emblem is centred with an even margin on a plain black background.'},
    ],
    directions: ['red ink', 'with flames', 'forearm'],
    directionHint: 'try: red ink',
    seo: {
        title: 'Trishul designs: damru, Om and snake emblems',
        metaDescription: 'Nine trishul emblems of Lord Shiva in three styles: black tattoo flash, a die-cut sticker and an engraved gold mark on black.',
        h1: 'Trishul designs: with damru, with Om, with a snake',
        tagline: 'Nine single emblems, three finishes each: black tattoo flash, a sticker and gold on black.',
        intro: 'The trishul of Shiva drawn as a single emblem, for a sticker, a tattoo idea, a car decal or the centre of a Shivratri post. Each design is one trishul on a plain ground: with a damru tied to the shaft, with the Om sign where the prongs meet, or with a cobra wound up the shaft. Type a different detail, such as flames, and the same three finishes carry it.',
        useCases: [
            'A sticker or a car decal',
            'A tattoo idea to show a tattoo artist',
            'The centre of a Maha Shivratri post or card',
            'A wall print or an emblem for a shop',
        ],
        faq: [
            {q: 'Is this a tattoo I can have done?', a: 'The tattoo flash style is a drawing to show a tattoo artist. A tattoo artist will adapt it to skin and to the size you want.'},
            {q: 'What is the difference between the three finishes?', a: 'Tattoo flash is black outlines with heavy shading, the die-cut sticker is flat saffron, blue and white, and gold on black is a fine engraved line with metallic shading.'},
            {q: 'Can I print one as a sticker?', a: 'The die-cut style has a thick white border cut round the whole shape, so it can be printed and cut as a sticker.'},
        ],
    },
}
